import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from 'recharts';

interface SummaryData {
  inboundToday: number;
  inboundMonth: number;
  outboundToday: number;
  outboundMonth: number;
}

interface DailyData {
  date: string; // format: "2025-07-01"
  inbound: number;
  outbound: number;
}

interface StatusSummary {
  inbound: Record<string, number>;
  outbound: Record<string, number>;
}

const InboundOutboundPerformance = () => {
  const [summary, setSummary] = useState<SummaryData>({
    inboundToday: 0,
    inboundMonth: 0,
    outboundToday: 0,
    outboundMonth: 0,
  });

  const [dailyData, setDailyData] = useState<DailyData[]>([]);
  const [statusSummary, setStatusSummary] = useState<StatusSummary>({
    inbound: {},
    outbound: {},
  });

  const API = process.env.REACT_APP_API_URL;

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
      },
    };
  };

  useEffect(() => {
    const today = new Date();
    const month = today.toISOString().slice(0, 7); // YYYY-MM

    // Fetch summary
    axios
      .get(`${API}/analytics/inbound-outbound-summary?month=${month}`, getAuthHeader())
      .then((res) => setSummary(res.data))
      .catch((err) => console.error('❌ Failed to fetch summary', err));

    // Fetch daily chart data
    axios
      .get(`${API}/analytics/inbound-outbound-daily?month=${month}`, getAuthHeader())
      .then((res) => setDailyData(res.data))
      .catch((err) => console.error('❌ Failed to fetch daily data', err));

    // Fetch status summary
    axios
      .get(`${API}/analytics/status-summary?month=${month}`, getAuthHeader())
      .then((res) => setStatusSummary(res.data))
      .catch((err) => console.error('❌ Failed to fetch status summary', err));
  }, []);

  const formatDay = (dateStr: string) => {
    if (!dateStr || typeof dateStr !== 'string') return '';
    const [, , day] = dateStr.split('-');
    return day;
  };

  const renderStatusCards = (title: string, data: Record<string, number>, color: string) => {
    return (
      <div className="bg-white rounded shadow p-4">
        <h4 className="font-semibold text-sm mb-2">{title}</h4>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(data).map(([status, count]) => (
            <div key={status} className={`p-2 rounded ${color}`}>
              <p className="text-xs text-gray-600 capitalize">{status}</p>
              <p className="text-lg font-bold text-gray-900">{count}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card title="Inbound Today" value={summary.inboundToday} color="bg-blue-100" />
        <Card title="Inbound This Month" value={summary.inboundMonth} color="bg-blue-200" />
        <Card title="Outbound Today" value={summary.outboundToday} color="bg-green-100" />
        <Card title="Outbound This Month" value={summary.outboundMonth} color="bg-green-200" />
      </div>
      
	  {/* Status Summary Cards */}
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
          {renderStatusCards('Inbound Status (Now)', statusSummary.inbound, 'bg-blue-50')}
          {renderStatusCards('Outbound Status (Now)', statusSummary.outbound, 'bg-green-50')}
        </div>
      </div>
	  
      {/* Inbound Chart */}
      <div className="bg-white p-4 rounded shadow">
        <h3 className="text-base font-semibold mb-2">Inbound Daily (This Month)</h3>
        <div style={{ width: '100%', height: 300, fontSize: 13 }}>
          <ResponsiveContainer>
            <BarChart data={dailyData} >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={formatDay} 
			    tick={{ fontSize: 13 }}
			  />
              <YAxis 
			    tick={{ fontSize: 13 }}
			  />
              <Tooltip />
              <Legend />
              <Bar dataKey="inbound" fill="#3b82f6" name="Inbound" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Outbound Chart */}
      <div className="bg-white p-4 rounded shadow">
        <h3 className="text-base font-semibold mb-2">Outbound Daily (This Month)</h3>
        <div style={{ width: '100%', height: 300, fontSize: 13 }}>
          <ResponsiveContainer>
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={formatDay} 
			    tick={{ fontSize: 13 }}
			  />
              <YAxis 
			    tick={{ fontSize: 13 }}
			  />
              <Tooltip />
              <Legend />
              <Bar dataKey="outbound" fill="#10b981" name="Outbound" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>


    </div>
  );
};

const Card = ({
  title,
  value,
  color,
}: {
  title: string;
  value: number;
  color: string;
}) => (
  <div className={`p-4 rounded shadow ${color}`}>
    <h3 className="text-sm text-gray-700 font-medium">{title}</h3>
    <p className="text-2xl font-bold text-gray-800">{value}</p>
  </div>
);

export default InboundOutboundPerformance;
